import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Send } from 'lucide-react';

const CommentComposer = ({ onSubmit, loading, error }) => {
  const { user, isBanned } = useAuth();
  const [text, setText] = useState('');

  if (!user) return null; // Or show a prompt to login

  const maxChars = 1000;
  const currentChars = text.length;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim() || currentChars > maxChars || loading || isBanned) return;

    onSubmit(text.trim());
    setText('');
  };

  let counterColor = 'text-gray-400';
  if (currentChars >= 990) counterColor = 'text-red-500 font-bold';
  else if (currentChars >= 950) counterColor = 'text-amber-500';

  return (
    <div className="flex gap-3 items-start">
      <div className="w-10 h-10 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center shrink-0 text-base">
        {user.name ? user.name.charAt(0).toUpperCase() : '?'}
      </div>

      <div className="flex-1 min-w-0">
        <form onSubmit={handleSubmit} className="relative">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write a comment…"
            maxLength={maxChars + 10} // Allow typing a bit over to show red state if pasting
            className={`w-full bg-white border ${error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-200 focus:ring-teal-500 focus:border-teal-500'} text-gray-800 py-3 pl-4 pr-28 rounded-xl text-sm shadow-sm focus:outline-none focus:ring-1 resize-none placeholder:text-gray-400`}
            rows={Math.min(4, Math.max(1, text.split('\n').length))}
            disabled={loading || isBanned}
          />

          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

          <div className={`absolute right-3 ${error ? 'bottom-9' : 'bottom-3'} flex items-center gap-2 bg-white px-1`}>
            {currentChars > 0 && (
              <span className={`text-xs ${counterColor}`}>
                {currentChars}/{maxChars}
              </span>
            )}
            <button
              type="submit"
              aria-label="Post comment"
              disabled={!text.trim() || currentChars > maxChars || loading || isBanned}
              className="cursor-pointer p-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Send size={16} />
              )}
            </button>
          </div>
        </form>
        <p className="mt-1.5 text-[11px] text-gray-400">Press Enter to post · Shift + Enter for a new line</p>
      </div>
    </div>
  );
};

export default CommentComposer;
